import {
  Box,
  Button,
  Checkbox,
  Flex,
  Heading,
  Icon,
  Input,
  List,
  ListIcon,
  ListItem,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react'
import { format, parse } from 'date-fns'
import { useContext, useEffect, useState } from 'react'
import { RiAddLine, RiCheckLine, RiDeleteBin6Line, RiFilePaper2Line, RiPencilLine } from 'react-icons/ri'
import InputMask from 'react-input-mask'
import { useMutation } from 'react-query'
import { Link } from 'react-router-dom'
import { Modal } from '../../components/Modal'
import { Pagination } from '../../components/Pagination'
import { SearchContext } from '../../context/SearchContext'
import { IEmployees } from '../../Interfaces/employees'
import { api } from '../../services/api'
import { useEmployees } from '../../services/hooks/useEmployees'

export function Employees() {
  const { searchValue } = useContext(SearchContext)
  const toast = useToast()
  const [isDesabilitarBotao, setDesabilitarBotao] = useState(false)
  const [page, setPage] = useState(1)
  const [dataInicio, setDataInicio] = useState(
    format(new Date(new Date().getFullYear(), new Date().getMonth(), 1), 'dd/MM/yyyy'),
  )
  const [dataFim, setDataFim] = useState(
    format(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0), 'dd/MM/yyyy'),
  )

  const { queryContextParams, clearQuery } = useContext(SearchContext)

  const { data, isLoading, isFetching, error, refetch } = useEmployees<IEmployees[]>({
    isSelect: false,
    page,
    ...queryContextParams,
  })

  const { isOpen, onOpen, onClose } = useDisclosure()

  const [currentEmployees, setCurrentEmployees] = useState<IEmployees>()

  useEffect(() => {
    refetch()
  }, [queryContextParams])

  useEffect(() => {
    return () => {
      clearQuery()

      refetch()
    }
  }, [])

  const handleRemoveButton = () => {
    if (currentEmployees) removeProvider.mutateAsync(currentEmployees)

    onClose()
  }

  const removeProvider = useMutation(
    async (employee: IEmployees) => {
      const response = await api.delete(`employees/${employee.id}`)

      if (response) {
        toast({ description: 'Removido com sucesso', status: 'success' })
        refetch()
      }

      onClose()
      return response.data
    },
    {
      onError: ({ response }) => {
        const errorList = response.data.errors

        const toastOptions = errorList.map((err) => ({ description: err.message, status: 'error' }))
        toastOptions.forEach((t) => {
          toast(t)
        })
      },
    },
  )

  const handleModalOpen = (employee) => {
    setCurrentEmployees(employee)

    onOpen()
  }

  const handlePrintOrder = async () => {
    setDesabilitarBotao(true)

    // Formatar as datas para o formato 'yyyy-MM-dd'
    const formattedDataInicio = format(parse(dataInicio, 'dd/MM/yyyy', new Date()), 'yyyy-MM-dd')
    const formattedDataFim = format(parse(dataFim, 'dd/MM/yyyy', new Date()), 'yyyy-MM-dd')

    // Realiza a requisição para obter o relatório em PDF e trata possíveis erros
    let response
    try {
      response = await api.get('/relatorio/horas-trabalhadas', {
        responseType: 'blob',
        params: {
          startDate: formattedDataInicio,
          endDate: formattedDataFim,
          employeeName: searchValue ? searchValue : undefined,
        },
      })
    } catch (error: any) {
      // Comentário: Tratamento de exceção caso a requisição falhe totalmente
      let errorMessage = 'Erro ao gerar o relatório de horas trabalhadas!'
      if (error.response && error.response.data) {
        const contentType = error.response.headers['content-type']
        if (contentType && contentType.includes('application/json')) {
          // Comentário: Como a resposta de erro vem como blob, converte para texto e extrai a mensagem
          const errorText = await error.response.data.text()
          try {
            const jsonResponse = JSON.parse(errorText)
            errorMessage = jsonResponse.message || errorMessage
          } catch {
            // Se ocorrer erro no parse, mantém a mensagem padrão
          }
        }
      }
      toast({
        description: errorMessage,
        status: 'error',
      })
      setDesabilitarBotao(false)
      return
    }

    const file = new Blob([response.data], { type: 'application/pdf' })

    window.open(URL.createObjectURL(file), '_blank')
    setDesabilitarBotao(false)
  }

  // Removido filtro local - o filtro deve ser aplicado no servidor via queryContextParams

  return (
    <Box flex="1" borderRadius="8" bg="gray.800" p="8">
      <Flex mb="8" justifyContent="space-between" align="center">
        <Heading size="lg" fontWeight="normal">
          Funcionários
          {!isLoading && isFetching && <Spinner size="sm" color="gray.500" ml="4" />}
        </Heading>

        <Input
          placeholder="Período Inicial"
          defaultValue={dataInicio}
          as={InputMask}
          onChange={(e) => setDataInicio(e.target.value)}
          mask="99/99/9999"
          width="13vh"
        />
        <Input
          placeholder="Período Final"
          as={InputMask}
          defaultValue={dataFim}
          onChange={(e) => setDataFim(e.target.value)}
          mask="99/99/9999"
          width="13vh"
        />
        <Button
          disabled={isDesabilitarBotao}
          size="sm"
          fontSize="sm"
          colorScheme="blue"
          leftIcon={<Icon as={RiFilePaper2Line} fontSize="20" />}
          onClick={handlePrintOrder}>
          Relatório Horas Trabalhadas
        </Button>

        <Button
          as={Link}
          to="/employees/create"
          size="sm"
          fontSize="sm"
          colorScheme="pink"
          leftIcon={<Icon as={RiAddLine} fontSize="20" />}>
          Criar Novo
        </Button>
      </Flex>

      {isLoading ? (
        <Flex justify="center">
          <Spinner />
        </Flex>
      ) : error ? (
        <Flex justify="center">
          <Text>Falha ao obter os funcionários</Text>
        </Flex>
      ) : (
        <Table colorScheme="whiteAlpha">
          <Thead>
            <Tr>
              <Th px={['4', '4', '6']} color="gray.300" width="8">
                <Checkbox colorScheme="pink" />
              </Th>
              <Th>Nome</Th>
              <Th>E-mail</Th>
              <Th>CPF</Th>
              <Th>Status</Th>
              <Th width="4" />
              <Th width="4" />
            </Tr>
          </Thead>
          <Tbody>
            {data?.employees.map((employee: IEmployees) => {
              return (
                <Tr key={employee.id}>
                  <Td px={['4', '4', '6']}>
                    <Checkbox colorScheme="pink" />
                  </Td>
                  <Td>{employee.name}</Td>
                  <Td>{employee.email}</Td>
                  <Td>{employee.phone}</Td>
                  <Td>{employee.status}</Td>
                  <Td>
                    <Button
                      as={Link}
                      to={`/employees/edit/${employee.id}`}
                      size="sm"
                      fontSize="sm"
                      colorScheme="purple"
                      leftIcon={<Icon as={RiPencilLine} />}>
                      Editar
                    </Button>
                  </Td>
                  <Td>
                    <Button
                      onClick={() => handleModalOpen(employee)}
                      size="sm"
                      fontSize="sm"
                      colorScheme="red"
                      leftIcon={<Icon as={RiDeleteBin6Line} />}>
                      Remover
                    </Button>
                  </Td>
                </Tr>
              )
            })}
          </Tbody>
        </Table>
      )}

      <Pagination totalCountOfRegisters={data?.totalCount} currentPage={page} onPageChange={setPage} />

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Remover Funcionário"
        buttonTitle="Remover"
        buttonAction={handleRemoveButton}>
        <List spacing={3}>
          <ListItem>
            <ListIcon as={RiCheckLine} color="green.500" />
            {`Nome: ${currentEmployees?.name}`}
          </ListItem>
          <ListItem>
            <ListIcon as={RiCheckLine} color="green.500" />
            {`CPF: ${currentEmployees?.cpf}`}
          </ListItem>
        </List>
      </Modal>
    </Box>
  )
}
